import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';
import { captureMessage, setContext } from './sentry';

// Web Vitals thresholds (good/needs improvement/poor)
const THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  INP: { good: 200, poor: 500 },
  LCP: { good: 2500, poor: 4000 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
};

type MetricRating = 'good' | 'needs-improvement' | 'poor';

function roundedMetricValue(name: Metric['name'], value: number): number {
  return Math.round(name === 'CLS' ? value * 1000 : value);
}

function formattedMetricValue(name: Metric['name'], value: number): string {
  return value.toFixed(name === 'CLS' ? 3 : 0);
}

function metricUnit(name: Metric['name']): string {
  return name === 'CLS' ? '' : 'ms';
}

function ratingEmoji(rating: Metric['rating']): string {
  if (rating === 'good') return '✅';
  if (rating === 'needs-improvement') return '⚠️';
  return '❌';
}

/**
 * Rate a metric value against thresholds
 */
function rateMetric(name: keyof typeof THRESHOLDS, value: number): MetricRating {
  const threshold = THRESHOLDS[name];
  if (!threshold) return 'good';

  if (value <= threshold.good) return 'good';
  if (value >= threshold.poor) return 'poor';
  return 'needs-improvement';
}

function logMetric(metric: Metric) {
  if (!import.meta.env.DEV) return;
  console.log(
    `[Web Vitals] ${ratingEmoji(metric.rating)} ${metric.name}: ${formattedMetricValue(metric.name, metric.value)}${metricUnit(metric.name)} (${metric.rating})`,
  );
}

function reportMetricToSentry(metric: Metric) {
  setContext('webVitals', {
    [metric.name]: {
      value: roundedMetricValue(metric.name, metric.value),
      rating: metric.rating,
    },
  });

  if (metric.rating === 'poor' && import.meta.env.PROD) {
    captureMessage(
      `Poor Web Vital: ${metric.name} = ${formattedMetricValue(metric.name, metric.value)}`,
      'warning',
    );
  }
}

function reportMetricToGoogleAnalytics(metric: Metric) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', metric.name, {
    event_category: 'Web Vitals',
    event_label: metric.id,
    value: roundedMetricValue(metric.name, metric.value),
    metric_rating: metric.rating,
    navigation_type: metric.navigationType,
    non_interaction: true,
  });
}

function reportMetricToCustomEndpoint(metric: Metric) {
  const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  if (!analyticsEndpoint || !import.meta.env.PROD) return;

  const data = JSON.stringify({
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    id: metric.id,
    navigationType: metric.navigationType,
    url: window.location.href,
    timestamp: Date.now(),
  });

  if (navigator.sendBeacon) {
    navigator.sendBeacon(analyticsEndpoint, data);
    return;
  }

  fetch(analyticsEndpoint, {
    method: 'POST',
    body: data,
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
  }).catch(() => {
    // Analytics failures must not affect the application.
  });
}

/**
 * Report a Web Vital metric
 */
function reportMetric(metric: Metric) {
  logMetric(metric);
  reportMetricToSentry(metric);
  reportMetricToGoogleAnalytics(metric);
  reportMetricToCustomEndpoint(metric);
}

/**
 * Initialize Web Vitals monitoring
 * Tracks Core Web Vitals and other performance metrics
 */
export function initWebVitals() {
  // Core Web Vitals
  onCLS(reportMetric); // Cumulative Layout Shift
  onINP(reportMetric); // Interaction to Next Paint (replaced FID)
  onLCP(reportMetric); // Largest Contentful Paint

  // Additional metrics
  onFCP(reportMetric); // First Contentful Paint
  onTTFB(reportMetric); // Time to First Byte

  if (import.meta.env.DEV) {
    console.log('[Web Vitals] Initialized - monitoring CLS, INP, LCP, FCP, TTFB');
  }
}

/**
 * Get current performance summary
 * Useful for debugging or displaying to users
 */
export function getPerformanceSummary(): Record<string, { value: number; rating: MetricRating }> {
  const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  const navEntry = entries[0];

  if (!navEntry) {
    return {};
  }

  const ttfb = navEntry.responseStart - navEntry.requestStart;
  const fcp = performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 0;

  return {
    TTFB: { value: ttfb, rating: rateMetric('TTFB', ttfb) },
    FCP: { value: fcp, rating: rateMetric('FCP', fcp) },
  };
}

// Type declaration for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}
